import { DataTable, TableMenu } from '@/components'
import type { TableMenuItem } from '@/components'
import { cn } from '@/lib/utils'
import type { VehicleExpirationNode } from '@/modules/vehicles'
import {
  EXPIRATION_TYPES,
  getVehicleExpirationStatus,
} from '@/modules/vehicles'
import { PERMISSIONS, usePermissions } from '@/utils'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { ExpirationStatusBadge } from '@/modules/vehicles/list-filters'
import { DynamicTable } from '@/modules/dynamic_tables'
import {
  MENU_COLUMN_ID,
  resolveVehicleColumns,
  useVehicleColumnLabels,
  useVehicleTableColumns,
  VehicleColumn,
} from '@/modules/vehicles/table-config'
import { getVehicleExpirationTypeName } from '../util'

interface Props {
  expirations: VehicleExpirationNode[]
  loading?: boolean
  /** Off on a single vehicle's own tab, where every row is that vehicle. */
  showVehicle?: boolean
  onEdit(expiration: VehicleExpirationNode): void
  onDelete(expiration: VehicleExpirationNode): void
  onMoveToServices(expiration: VehicleExpirationNode): void
}

export const VehicleExpirationsTable: React.FC<Props> = ({
  expirations,
  loading = false,
  showVehicle = true,
  onEdit,
  onDelete,
  onMoveToServices,
}) => {
  const translations = useTranslate({
    noExpirationDates: 'vehicles.no-expiration-dates',
    edit: 'common.edit',
    delete: 'common.delete',
    moveToServices: 'vehicles.expirations.move-to-services',
    items: ['vehicles.expirations.num-expiration-dates', { n: 0 }],
  })

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  const { config, loading: configLoading } = useVehicleTableColumns(
    DynamicTable.VEHICLE_EXPIRATIONS
  )
  const labels = useVehicleColumnLabels()[DynamicTable.VEHICLE_EXPIRATIONS]

  const column = createColumnHelper<VehicleExpirationNode>()

  const columnDefs = useMemo(() => {
    const defs = [
      column.accessor('vehicle', {
        id: VehicleColumn.VEHICLE,
        header: labels[VehicleColumn.VEHICLE],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.vehicle?.registrationNumber ?? '').localeCompare(
            b.original.vehicle?.registrationNumber ?? ''
          ),
        cell(info) {
          const vehicle = info.getValue()
          if (!vehicle) return '-'

          return (
            <Link className="text-inherit" to={`/vehicle-fleet/${vehicle.id}`}>
              {vehicle.registrationNumber || '-'}
            </Link>
          )
        },
      }),
      column.accessor('expirationType', {
        id: VehicleColumn.TYPE,
        header: labels[VehicleColumn.TYPE],
        enableSorting: true,
        sortingFn: (a, b) =>
          getVehicleExpirationTypeName({
            expiration: a.original,
          }).localeCompare(
            getVehicleExpirationTypeName({ expiration: b.original })
          ),
        cell: info =>
          getVehicleExpirationTypeName({ expiration: info.row.original }),
      }),
      column.accessor('description', {
        id: VehicleColumn.DESCRIPTION,
        header: labels[VehicleColumn.DESCRIPTION],
        cell: info => info.getValue() || '-',
      }),
      column.accessor('expirationDate', {
        id: VehicleColumn.EXPIRATION_DATE,
        header: labels[VehicleColumn.EXPIRATION_DATE],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.expirationDate).getTime() -
          new Date(b.original.expirationDate).getTime(),
        cell(info) {
          const expirationDate = info.getValue()
          if (!expirationDate) return '-'
          return format(new Date(expirationDate), 'PP')
        },
      }),
      column.display({
        id: VehicleColumn.STATUS,
        header: labels[VehicleColumn.STATUS],
        meta: { narrow: true },
        cell(info) {
          const status = getVehicleExpirationStatus(
            info.row.original.expirationDate
          )
          return <ExpirationStatusBadge status={status} />
        },
      }),
      column.accessor('notificationTime', {
        id: VehicleColumn.NOTIFICATION_TIME,
        header: labels[VehicleColumn.NOTIFICATION_TIME],
        meta: { narrow: true },
        cell(info) {
          const { expirationDate } = info.row.original
          const notificationTime = info.getValue()
          if (!notificationTime || !expirationDate) return '-'

          return formatDistanceStrict(
            new Date(expirationDate),
            new Date(notificationTime)
          )
        },
      }),
      column.accessor('responsibleUser', {
        id: VehicleColumn.RESPONSIBLE_USER,
        header: labels[VehicleColumn.RESPONSIBLE_USER],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.responsibleUser?.fullName ?? '').localeCompare(
            b.original.responsibleUser?.fullName ?? ''
          ),
        cell: info => info.getValue()?.fullName || '-',
      }),
      column.display({
        id: MENU_COLUMN_ID,
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const expiration = info.row.original
          // The EU control date is derived from vegvesen data, so it must not
          // be deleted here — same rule as the vehicle detail page.
          const isEuControl =
            getVehicleExpirationTypeName({ expiration }) ===
            EXPIRATION_TYPES.nextEuControl

          const items: TableMenuItem[] = []
          if (canEdit) {
            items.push({
              id: 'edit',
              type: 'regular',
              label: translations.edit,
              icon: 'edit',
              onClick: () => onEdit(expiration),
            })
          }
          // Moving a row creates one and deletes another, so it takes both
          // rights. The EU control date is owned by Vegvesen, which would
          // recreate the expiration on the next vehicle refresh.
          if (canAdd && canDelete && !isEuControl) {
            items.push({
              id: 'move-to-services',
              type: 'regular',
              label: translations.moveToServices,
              icon: 'wrench',
              onClick: () => onMoveToServices(expiration),
            })
          }
          if (canDelete && !isEuControl) {
            items.push({
              id: 'delete',
              type: 'regular',
              label: translations.delete,
              icon: 'trash',
              onClick: () => onDelete(expiration),
            })
          }
          if (items.length === 0) return null

          return <TableMenu items={items} />
        },
      }),
    ]

    return showVehicle
      ? defs
      : defs.filter(def => def.id !== VehicleColumn.VEHICLE)
  }, [
    canAdd,
    canDelete,
    canEdit,
    column,
    labels,
    onDelete,
    onEdit,
    onMoveToServices,
    showVehicle,
    translations.delete,
    translations.edit,
    translations.moveToServices,
  ])

  const columns = useMemo(
    () => resolveVehicleColumns(columnDefs, config),
    [columnDefs, config]
  )

  return (
    <DataTable
      className={cn('w-full')}
      columns={columns}
      data={expirations}
      loading={loading || configLoading}
      inCard
      initialSort={[{ id: VehicleColumn.EXPIRATION_DATE, desc: false }]}
      noDataText={translations.noExpirationDates}
      pagination={{ itemsText: n => translations.items({ n }) }}
    />
  )
}
